import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto } from '@picflow/types';
import { firstValueFrom } from 'rxjs';
import { getFileContentType } from '../lib/get-file-type';
import { loadScript } from '../lib/load-script';
import { PICFLOW_CONFIG } from '../picflow-config';
import { DataLoaderService } from './data-loader.service';
import { StorageService } from './storage.service';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gapi: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google: any;
  }
}

export interface GoogleDriveFile {
  name: string;
  id: string;
  size: number;
  source: 'google-drive';
}

const DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest';
const API_KEY = 'AIzaSyA3QtG-HpoiklX8jid6_AriI9ToWlEbzfg';
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';
const CLIENT_ID = '387343500342-obn0crc436d0kubai59g2frmat1sbmvl.apps.googleusercontent.com';

@Injectable({
  providedIn: 'root',
})
export class GoogleDriveService {
  private storage = inject(StorageService);
  private http = inject(HttpClient);
  private config = inject(PICFLOW_CONFIG);
  private loader = inject(DataLoaderService);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tokenClient: any;

  private async loadGoogleClient() {
    await this.loader.get('google-drive-client', async () => {
      await loadScript('https://apis.google.com/js/api.js');
      await loadScript('https://accounts.google.com/gsi/client');
      await this.loadGapi();

      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
      });
    });
  }

  private async loadGapi() {
    return new Promise<void>(resolve => {
      window.gapi.load('client', async () => {
        await window.gapi.client.init({
          apiKey: API_KEY,
          discoveryDocs: [DISCOVERY_DOC],
        });
        resolve();
      });
    });
  }

  public async authenticate() {
    await this.loadGoogleClient();

    const token = window.gapi.client.getToken();
    if (token) return token;

    return new Promise<boolean>(resolve => {
      this.tokenClient.callback = async () => {
        resolve(window.gapi.client.getToken());
      };

      const token = window.gapi.client.getToken();

      if (!token) {
        this.tokenClient.requestAccessToken({ prompt: 'consent' });
      } else {
        this.tokenClient.requestAccessToken({ prompt: '' });
      }
    });
  }

  public async listFiles(path = '') {
    const authed = await this.authenticate();
    if (!authed) return;

    const response = await window.gapi.client.drive.files.list({
      pageSize: 1000,
      q: path ? `'${path}' in parents` : undefined,
      fields: 'files(id, name, mimeType, size)',
    });

    return response?.result?.files.map(entry => ({
      source: 'google-drive',
      ...entry,
      size: Number(entry.size),
    }));
  }

  public async uploadFile(
    file: GoogleDriveFile,
    galleryId: string,
    sectionId: string,
    position: number,
    assetToReplace?: AssetDto
  ): Promise<AssetDto> {
    const token = await this.authenticate();

    await window.gapi.client.drive.files.get({
      fileId: file.id,
      alt: 'media',
    });

    return firstValueFrom(
      this.http.post<AssetDto>(this.config.internalApiUrlBase + '/upload-from-google-drive?with-auth=true', {
        gallery: galleryId,
        section: sectionId,
        asset_name: file.name,
        position,
        id: file.id,
        asset_to_replace: assetToReplace?.id,
        googleDriveToken: token,
        content_type: getFileContentType(file),
      })
    );
  }
}
